/* eslint-disable @typescript-eslint/no-shadow */
import React, {useEffect, useState} from 'react';
import {useSnackbar} from 'notistack';
import {useLocation} from 'react-router';
import CheckboxTree from 'react-checkbox-tree';
import {useTranslation} from 'react-i18next';

import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useSelectedDocumentContext} from '@context/SelectedDocumentContext';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import {getDocumentsTreeData} from '@shared/services/service-handlers/PrivateService';
import {BASE_MENU_ITEM} from 'src/routes/BaseRoute';
import EncryptUtils from 'src/utils/EncryptUtils';
import AuthUtils from 'src/utils/AuthUtils';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
// import theme from 'src/styles/theme';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {Grid, useTheme} from '@mui/material';
import {LoaderGrid} from './ClinicalReportDocumentStyles';
import FileThumbnail from 'src/minimal-components/file-thumbnail/file-thumbnail';
import FormConstants from '@shared/constants/FormConstants';

const ClinicalReportDocument = (props: any): React.ReactElement => {
  const theme = useTheme();
  const {enqueueSnackbar} = useSnackbar();
  const [checked, setChecked] = useState<any>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [expanded, setExpanded] = useState<any>([]);
  const [nodesData, setNodesData] = useState<any>([]);
  const [originalNodesData, setOriginalNodesData] = useState<any>([]);
  const [loader, setLoader] = useState<any>(true);
  const location = useLocation();
  const {t} = useTranslation();
  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);
  const {
    SelectedDocumentState,
    setSelectedDocumentData,
    clearSelectedDocumentData
  } = useSelectedDocumentContext();
  const getLocaleStorageData = AuthUtils.getSelectedCheckBoxData();
  const {
    subModuleUIData,
    selectedDropdownValue,
    isConfirmed,
    setIsConfirmed,
    selectedDateValue,
    selectedRadioOption,handleCountUpdate
  } = props;
  const {
    document_folders_sql: documentFolderSql,
    legit_documents_folders_sql: legitDocumentsFoldersSql,
    documents_count_sql: documentsCountSql,
    variables
  } = subModuleUIData;
  const handleCheck = async (checkedValue: any, count?: number) => {
    setChecked(checkedValue);
    setSelectedDocumentData({data: checkedValue});

    // Store the combined checked count and total count in session storage
    const totalCountValue = count ?? totalCount ?? '';
    const checkedCountString = totalCountValue
      ? `${checkedValue.length}/${totalCountValue}`
      : `${checkedValue.length}`;
    sessionStorage.setItem('@checkedCount', checkedCountString);
    await handleCountUpdate();
  };

  const getPreCheckedDocuments = (
    item: any,
    SelectedEncountersList: any,
    preCheckedDocuments: any
  ) => {
    SelectedEncountersList.map((selectedEncounter: any) => {
      if (
        selectedEncounter?.EncounterNumber === item?.EncounterNumber &&
        selectedEncounter?.LegacyApp.toLowerCase() ===
          item?.LegacyApp.toLowerCase() &&
        selectedEncounter?.LegacyMRN === item?.LegacyMRN &&
        item?.FileExtension
      ) {
        preCheckedDocuments.push(item?.value);
      }
    });

    if (item?.children?.length > 0) {
      for (const key of item.children) {
        getPreCheckedDocuments(
          key,
          SelectedEncountersList,
          preCheckedDocuments
        );
      }
    }
  };
  const getPreCheckedDateDocuments = (
    item: any,
    selectedDateValue: any,
    preCheckedDocuments: any
  ) => {
    if (
      selectedDateValue?.start_date &&
      selectedDateValue?.stop_date &&
      item?.DocumentDate &&
      item?.FileExtension
    ) {
      const start = new Date(selectedDateValue.start_date);
      start.setHours(0, 0, 0, 0); // 00:00:00.000

      const end = new Date(selectedDateValue.stop_date);
      end.setHours(23, 59, 59, 999); // 23:59:59.999

      const docDate = new Date(item.DocumentDate);

      if (docDate >= start && docDate <= end) {
        preCheckedDocuments.push(item?.value);
      }
    }

    if (item?.children?.length > 0) {
      for (const key of item.children) {
        getPreCheckedDateDocuments(key, selectedDateValue, preCheckedDocuments);
      }
    }
  };
  // Filter documents based on the selected dropdown
  const filterTreeData = (treeData: any) => {
    if (selectedDropdownValue !== FormConstants.REPORTS_TERMS.ALL_SYSTEMS) {
      return treeData.filter(
        (item: any) =>
          item.label?.toLowerCase() === selectedDropdownValue.toLowerCase()
      );
    }
    return treeData;
  };

  const init = async () => {
    setLoader(true);
    try {
      const sql = {
        documentFolderSql: `${documentFolderSql}`,
        legitDocumentFoldersSql: `${legitDocumentsFoldersSql}`,
        documentsCountSql: `${documentsCountSql}`,
        folderContentsQuery: subModuleUIData?.document_folders_contents
      };

      const data: any = {
        sql: sql,
        variables: variables,
        filter_schema_clause: subModuleUIData?.filter_schema_clause
      };

      const encryptRequestData = EncryptUtils.encryptedData(data);
      const res = await getDocumentsTreeData(
        props?.module,
        props?.sub_module,
        props?.patient_id,
        {tk: encryptRequestData},
        props?.legacyApp,
        props?.unique_id,
        menuItem || ''
      );
      const treeData = res?.data;
      // Store original data for filtering
      setOriginalNodesData(treeData);
      setTotalCount(res?.count);
      const checkedCountString = res?.count ? `0/${res?.count}` : '0';
      sessionStorage.setItem('@checkedCount', checkedCountString);
      // Filter locally instead of refetching
      setNodesData(filterTreeData(treeData));

      // First, check if there's any data stored in the context (SelectedDocumentState)
      const storedSelection = SelectedDocumentState?.selectionData || [];

      if (storedSelection.length > 0) {
        // If there's context data, use it to pre-check the documents
        handleCheck(storedSelection, res?.count);
      } else {
        // Otherwise, fallback to local storage and pre-check documents
        const filterData = getLocaleStorageData
          ? JSON.parse(getLocaleStorageData)
          : [];

        if (filterData?.length > 0) {
          const preCheckedDocuments: any = [];
          const SelectedEncountersList = filterData?.map((item: any) => {
            return {
              EncounterNumber: item?.EncounterNumber,
              LegacyApp: item?.LegacyApp,
              LegacyMRN: item?.LegacyMRN
            };
          });

          for (const item of treeData) {
            getPreCheckedDocuments(
              item,
              SelectedEncountersList,
              preCheckedDocuments
            );
          }

          handleCheck(preCheckedDocuments, res?.count);
        }
        if (
          selectedDateValue?.start_date !== null &&
          selectedDateValue?.start_date !== '' &&
          selectedDateValue?.end_date !== null &&
          selectedDateValue?.end_date !== ''
        ) {
          const preCheckedDocuments: any = [];

          for (const item of treeData) {
            getPreCheckedDateDocuments(
              item,
              selectedDateValue,
              preCheckedDocuments
            );
          }
          handleCheck(preCheckedDocuments, res?.count);
        }
      }
      handleCountUpdate();
      setLoader(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLoader(false);
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (
      selectedDateValue?.start_date !== null &&
      selectedDateValue?.start_date !== '' &&
      selectedDateValue?.end_date !== null &&
      selectedDateValue?.end_date !== '' &&
      originalNodesData.length > 0
    ) {
      const preCheckedDocuments: any = [];
      for (const item of filterTreeData(originalNodesData)) {
        getPreCheckedDateDocuments(
          item,
          selectedDateValue,
          preCheckedDocuments
        );
      }
      handleCheck(preCheckedDocuments, totalCount);
      handleCountUpdate();
    }
  }, [selectedDateValue]);
  // Apply filter whenever the selectedDropdownValue changes
  useEffect(() => {
    if (isConfirmed) {
      const filterData = getLocaleStorageData
        ? JSON.parse(getLocaleStorageData)
        : [];
      if (filterData?.length > 0) {
        const preCheckedDocuments: any = [];
        const SelectedEncountersList = filterData?.map((item: any) => {
          return {
            EncounterNumber: item?.EncounterNumber,
            LegacyApp: item?.LegacyApp,
            LegacyMRN: item?.LegacyMRN
          };
        });

        for (const item of filterTreeData(originalNodesData)) {
          getPreCheckedDocuments(
            item,
            SelectedEncountersList,
            preCheckedDocuments
          );
        }

        handleCheck(preCheckedDocuments, totalCount);
      }
      if (
        selectedDateValue?.start_date !== null &&
        selectedDateValue?.start_date !== '' &&
        selectedDateValue?.end_date !== null &&
        selectedDateValue?.end_date !== ''
      ) {
        const preCheckedDocuments: any = [];

        for (const item of filterTreeData(originalNodesData)) {
          getPreCheckedDateDocuments(
            item,
            selectedDateValue,
            preCheckedDocuments
          );
        }
        handleCheck(preCheckedDocuments, totalCount);
      }
      // clearSelectedDocumentData({data: []});
      // sessionStorage.removeItem('@checkedCount');
      // const checkedCountString = totalCount ? `0/${totalCount}` : `0`;
      // sessionStorage.setItem('@checkedCount', checkedCountString);
      // handleCountUpdate();
    }
    setNodesData(filterTreeData(originalNodesData));
    setIsConfirmed(false);
  }, [selectedDropdownValue, isConfirmed]);
  useEffect(() => {
    const filterData = getLocaleStorageData
      ? JSON.parse(getLocaleStorageData)
      : [];

    if (
      filterData?.length > 0 &&
      selectedRadioOption === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
    ) {
      const preCheckedDocuments: any = [];
      const SelectedEncountersList = filterData?.map((item: any) => {
        return {
          EncounterNumber: item?.EncounterNumber,
          LegacyApp: item?.LegacyApp,
          LegacyMRN: item?.LegacyMRN
        };
      });

      for (const item of filterTreeData(originalNodesData)) {
        getPreCheckedDocuments(
          item,
          SelectedEncountersList,
          preCheckedDocuments
        );
      }

      handleCheck(preCheckedDocuments, totalCount);
    }
    if (
      selectedDateValue?.start_date !== null &&
      selectedDateValue?.start_date !== '' &&
      selectedDateValue?.end_date !== null &&
      selectedDateValue?.end_date !== '' &&
      selectedRadioOption ===
        FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE
    ) {
      const preCheckedDocuments: any = [];

      for (const item of filterTreeData(originalNodesData)) {
        getPreCheckedDateDocuments(
          item,
          selectedDateValue,
          preCheckedDocuments
        );
      }
      handleCheck(preCheckedDocuments, totalCount);
    }
    handleCountUpdate();
  }, [selectedRadioOption]);
  const icons = {
    check: (
      <CheckBoxIcon
        sx={{
          color: theme.palette.primary.main
        }}
        className="rct-icon rct-icon-check"
      />
    ),
    uncheck: (
      <CheckBoxOutlineBlankIcon
        style={{
          color: theme.palette.grey[600]
        }}
        className="rct-icon rct-icon-uncheck"
      />
    ),
    halfCheck: (
      <IndeterminateCheckBoxIcon
        sx={{
          color: theme.palette.primary.main
        }}
        className="rct-icon rct-icon-half-check"
      />
    ),
    expandClose: (
      <ArrowRightIcon
        sx={{
          color: theme.palette.grey[600]
        }}
        className="rct-icon rct-icon-expand-open"
      />
    ),
    expandOpen: (
      <ArrowDropDownIcon
        sx={{
          color: theme.palette.grey[600]
        }}
        className="rct-icon rct-icon-expand-close"
      />
    ),
    parentClose: (
      <FileThumbnail
        file={'folder'}
        sx={{width: 20, height: 20, marginBottom: '4px'}}
      />
    ),
    parentOpen: (
      <FileThumbnail
        file={'openfolder'}
        sx={{width: 20, height: 20, marginBottom: '4px'}}
      />
    ),
    leaf: (
      <FileThumbnail
        file={'file'}
        sx={{width: 20, height: 20, marginBottom: '4px'}}
      />
    )
  };

  const renderDocument = () => {
    if (loader) {
      return (
        <LoaderGrid>
          <ComponentLoader loaderSize={10} />
        </LoaderGrid>
      );
    }

    if (nodesData.length > 0) {
      return (
        <CheckboxTree
          nodes={nodesData}
          checked={checked}
          expanded={expanded}
          onCheck={isChecked => handleCheck(isChecked)}
          onExpand={isExpanded => setExpanded(isExpanded)}
          icons={icons}
        />
      );
    }

    return (
      <LoaderGrid>
        {t('PRIVATE.REPORTS.NEW_REPORTS.NO_DATA', {ns: 'private'})}
      </LoaderGrid>
    );
  };

  return <Grid>{renderDocument()}</Grid>;
};

export default ClinicalReportDocument;
